import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaMobile } from "react-icons/fa";
import TextLogo from "../components/TextLogo";
import { FaLocationDot } from "react-icons/fa6";
import { Helmet } from "react-helmet-async";
import { FaGamepad, FaBrain, FaGraduationCap, FaUser } from "react-icons/fa";

function HomePage() {
  const navigate = useNavigate();

  const services = [
    {
      title: "Bireysel Yetişkin Psikoterapisi",
      description: "Yetişkinler için profesyonel bireysel psikoterapi",
      path: "/bireysel-yetiskin-psikoterapisi",
      icon: FaUser,
    },
    {
      title: "MOXO Testi",
      description: "Dikkat ve odaklanma değerlendirmesi",
      path: "/moxo-testi",
      icon: FaBrain,
    },
    {
      title: "Öğrenci Danışmanlığı",
      description: "Akademik ve kişisel gelişim desteği",
      path: "/ogrenci-danismanligi",
      icon: FaGraduationCap,
    },
    {
      title: "Oyun Terapisi",
      description: "Çocuklar için terapötik destek",
      path: "/oyun-terapisi",
      icon: FaGamepad,
    },
  ];

  const goToAboutPage = () => {
    navigate("/hakkimda");
  };

  const goToContactPage = () => {
    navigate("/iletisim");
  };

  const goToBlogPage = () => {
    navigate("/blog");
  };

  const goToServicesPage = () => {
    navigate("/calisma-alanlarim");
  };

  useEffect(() => {
    const handleBackgroundColor = () => {
      if (window.innerWidth >= 768) {
        document.body.style.backgroundColor = "#fff";
      } else {
        document.body.style.backgroundColor = "";
      }
    };

    handleBackgroundColor();
    window.addEventListener("resize", handleBackgroundColor);

    return () => {
      window.removeEventListener("resize", handleBackgroundColor);
      document.body.style.backgroundColor = "";
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Psikolog Yağmur Demiröz | Bireysel Yetişkin Psikoterapisi, Ergen Psikoterapisi, Oyun Terapisi - İstanbul
        </title>
        <meta
          name="description"
          content="Psikolog Yağmur Demiröz - Bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi, MOXO testi ve psikolojik danışmanlık hizmetleri. İstanbul, Etiler, Ümraniye."
        />
        <meta name="keywords" content="bireysel yetişkin psikoterapisi, ergen psikoterapisi, yetişkin terapisi, bireysel terapi, oyun terapisi, İstanbul psikolog, psikoterapi, psikolojik danışmanlık, MOXO testi" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Psikolog Yağmur Demiröz" />
        <meta
          property="og:title"
          content="Psikolog Yağmur Demiröz – Bireysel Yetişkin Psikoterapisi, Ergen Psikoterapisi, Oyun Terapisi"
        />
        <meta
          property="og:description"
          content="Bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi ve psikolojik destek. İstanbul, Etiler, Ümraniye."
        />
        <meta property="og:image" content="https://yagmurdemiroz.com/me.jpg" />
        <meta property="og:url" content="https://yagmurdemiroz.com" />
        <link rel="canonical" href="https://yagmurdemiroz.com/" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Psychologist",
            "name": "Yağmur Demiröz",
            "url": "https://yagmurdemiroz.com",
            "jobTitle": "Psikolog",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Nispetiye",
              "addressLocality": "Etiler, İstanbul",
              "addressCountry": "TR"
            },
            "telephone": "+90 544 135 47 54",
            "email": "pskyagmurdemiroz@gmail.com",
            "description": "Psikolog Yağmur Demiröz, bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi, MOXO testi ve psikolojik danışmanlık hizmetleri sunmaktadır.",
            "knowsAbout": ["Bireysel Yetişkin Psikoterapisi", "Ergen Psikoterapisi", "Psikoterapi", "Bireysel Terapi", "MOXO Testi", "Oyun Terapisi", "Öğrenci Danışmanlığı"],
            "sameAs": [
              "https://www.instagram.com/psk.yagmurdemiroz",
              "https://www.linkedin.com/in/yagmurdemiroz"
            ]
          }
          `}
        </script>
      </Helmet>

      <div className="flex md:flex-row flex-col min-h-screen overflow-x-hidden relative">
        {/* Left Section: Navbar, Header & Contact */}
        <div className="bg-label stylized-box md:w-1/2 w-full md:min-h-screen right-polygon flex flex-col justify-center items-center relative overflow-hidden animate-fadeInLeft">
          <div className="relative z-10 w-full h-full flex flex-col justify-between md:p-6">
            <div className="p-4 flex flex-col justify-center flex-grow text-left animate-fadeInUp">
              <h1 className="text-2xl md:text-3xl text-secondary my-8 ms-5 font-medium leading-snug">
                <TextLogo color="secondary" size="responsive" />
              </h1>

              {/* Contact Information with enhanced styling */}
              <div className="bg-white/80 p-4 rounded-lg shadow-sm mb-6">
                {/* Email */}
                <p className="text-sm md:text-base text-gray-600 flex items-center bg-white/90 p-2 rounded mb-4">
                  <FaEnvelope className="mr-2 text-secondary" />
                  <strong className="mr-2">Email:</strong>
                  <a href="mailto:pskyagmurdemiroz@gmail.com" className="text-gray-800 hover:text-secondary transition-colors">
                    pskyagmurdemiroz@gmail.com
                  </a>
                </p>

                {/* Two Location Cards Side by Side */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Nispetiye/Etiler */}
                  <div className="bg-white/90 p-3 rounded border-l-4 border-secondary">
                    <p className="font-bold text-gray-800 mb-2">Nispetiye / Etiler</p>
                    <p className="text-sm text-gray-600 flex items-center mb-2">
                      <FaMobile className="mr-2 text-secondary" />
                      <a href="tel:+905441354754" className="text-gray-800 hover:text-secondary transition-colors">
                        +90 544 135 47 54
                      </a>
                    </p>
                    <p className="text-sm text-gray-600 flex items-start">
                      <FaLocationDot className="mr-2 text-secondary mt-0.5 flex-shrink-0" />
                      <a href="https://maps.app.goo.gl/sL9XxCRfdvr5y17P7" target="_blank" rel="noreferrer" className="text-gray-800 hover:text-secondary transition-colors">
                        Nispetiye / Etiler, İstanbul
                      </a>
                    </p>
                  </div>

                  {/* Ümraniye */}
                  <div className="bg-white/90 p-3 rounded border-l-4 border-secondary">
                    <p className="font-bold text-gray-800 mb-2">Ümraniye</p>
                    <p className="text-sm text-gray-600 flex items-center mb-2">
                      <FaMobile className="mr-2 text-secondary" />
                      <a href="tel:+905380792109" className="text-gray-800 hover:text-secondary transition-colors">
                        +90 538 079 21 09
                      </a>
                    </p>
                    <p className="text-sm text-gray-600 flex items-start">
                      <FaLocationDot className="mr-2 text-secondary mt-0.5 flex-shrink-0" />
                      <span className="text-gray-800">Ümraniye, İstanbul</span>
                    </p>
                  </div>
                </div>
              </div>


              {/* Services Section for desktop */}
              <div className="hidden md:block">
                <div className="grid md:grid-cols-3 gap-6">
                  {services.map((svc) => (
                    <div
                      key={svc.path}
                      onClick={() => navigate(svc.path)}
                      className="flex flex-col items-center bg-white border-b-2 border-secondary p-5 rounded-lg shadow-sm transition duration-200 hover:shadow-md cursor-pointer"
                      role="button"
                      aria-label={svc.title}
                    >
                      <div className="text-3xl mb-3 text-secondary">
                        <svc.icon aria-hidden="true" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2 text-center text-gray-700">
                        <a href={svc.path} className="hover:underline">
                          {svc.title}
                        </a>
                      </h3>
                      <p className="text-sm text-gray-600 text-center">
                        {svc.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section: Image and Menus */}
        <div className="md:w-1/2 w-full min-h-screen left-polygon flex items-center justify-center relative p-4 md:p-8 animate-fadeInRight">
          <div className="absolute inset-0 bg-secondary opacity-50"></div>
          <div
            className="absolute inset-0 bg-[url('assets/images/imagecopy.jpg')] bg-cover bg-center bg-no-repeat"
            style={{ mixBlendMode: "multiply" }}
          ></div>
          <div className="relative max-w-md text-center z-10">
            <div className="w-full max-w-xs md:max-w-sm mx-auto bg-transparent border border-primary rounded-lg shadow-lg animate-scaleIn">
              <div className="flex justify-end px-4 pt-4">
                <img
                  className="max-w-full h-auto rounded-lg shadow-md"
                  src={require("../assets/images/me.jpg")}
                  alt="Psikolog Yağmur Demiröz – Bireysel Yetişkin Psikoterapisi, Ergen Psikoterapisi, Oyun Terapisi"
                  loading="lazy"
                  width="300"
                  height="400"
                />
              </div>
              <div className="flex flex-col items-center p-5">
                <div className="flex flex-col space-y-3 w-full">
                  <button
                    onClick={goToServicesPage}
                    className="w-full bg-white border-b-2 border-secondary text-secondary py-3 px-6 rounded-md text-base font-semibold hover:bg-secondary hover:text-label hover:border-secondary transition-all duration-300 transform hover:-translate-y-0.5 animate-fadeInUp"
                    style={{ animationDelay: '0.1s' }}
                  >
                    Çalışma Alanlarım
                  </button>
                  <button
                    onClick={goToAboutPage}
                    className="w-full bg-white border-b-2 border-secondary text-secondary py-3 px-6 rounded-md text-base font-semibold hover:bg-secondary hover:text-label hover:border-secondary transition-all duration-300 transform hover:-translate-y-0.5 animate-fadeInUp"
                    style={{ animationDelay: '0.2s' }}
                  >
                    Hakkımda
                  </button>
                  <button
                    onClick={goToBlogPage}
                    className="w-full bg-white border-b-2 border-secondary text-secondary py-3 px-6 rounded-md text-base font-semibold hover:bg-secondary hover:text-label hover:border-secondary transition-all duration-300 transform hover:-translate-y-0.5 animate-fadeInUp"
                    style={{ animationDelay: '0.3s' }}
                  >
                    Blog
                  </button>
                  <button
                    onClick={goToContactPage}
                    className="w-full bg-white border-b-2 border-secondary text-secondary py-3 px-6 rounded-md text-base font-semibold hover:bg-secondary hover:text-label hover:border-secondary transition-all duration-300 transform hover:-translate-y-0.5 animate-fadeInUp"
                    style={{ animationDelay: '0.4s' }}
                  >
                    İletişim
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-only Services Section */}
      <div className="block md:hidden p-4 animate-fadeInUp">
        <div className="grid grid-cols-1 gap-6">
          {services.map((svc, index) => (
            <div
              key={svc.path}
              onClick={() => navigate(svc.path)}
              className="flex flex-col items-center bg-white p-5 rounded-lg shadow-sm transition duration-200 hover:shadow-md cursor-pointer animate-fadeInUp"
              style={{ animationDelay: `${index * 0.1}s` }}
              role="button"
              aria-label={svc.title}
            >
              <div className="text-3xl mb-3 text-secondary">
                <svc.icon aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-center text-gray-700">
                <a href={svc.path} className="hover:underline">
                  {svc.title}
                </a>
              </h3>
              <p className="text-sm text-gray-600 text-center">
                {svc.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default HomePage;
